import {
  complexDurationAndAttendeesServices,
  defaultService,
  extendedSingleDurationPaidService,
  multipleServices,
  multiTierService,
  poaService,
  serviceNoDescription,
  serviceWithUnsafeDescription,
  unavailableService,
} from "./service";
import {
  defaultOrganisation,
  organisationForcedTimezone,
  tenOrganisationsPage,
  testOrganisationWithFeatureFlag,
  twoOrganisationsDifferentTimezonePage,
  twoOrganisationsPage,
} from "./organisation";
import {
  GetBookingToolDataDocument,
  GetBookingToolDataQuery,
  GetBookingToolDataQueryVariables,
  ResourceSelectionMode,
  UnitOfMeasurement,
} from "../GeneratedGraphQL";

import { BookingTool } from "../AppDataProvider/AppDataProvider";
import { createPageInfo } from "./pageInfo";
import { MockedResponse } from "@apollo/client/testing";

export const defaultBookingTool = {
  _id: "1234567890abcdef12345678",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: false },
  mapkitAuthenticationToken: "testToken",
  name: "Test Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  percentageThresholdForShowingRemainingNumberOfSpaces: 15,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};
export const extendedServiceBookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [
      {
        cursor: extendedSingleDurationPaidService._id,
        node: extendedSingleDurationPaidService,
      },
    ],
    pageInfo: createPageInfo(),
  },
};

export const forcePhoneValidationBookingTool = {
  ...defaultBookingTool,
  _id: "2234567890abcdef12345333",
  forcePhoneValidationToCountry: "GB",
};

export const defaultBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: defaultBookingTool._id },
    },
    result: { data: { bookingTool: defaultBookingTool } },
  };

export const extendedServiceBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: extendedServiceBookingTool._id },
    },
    result: { data: { bookingTool: extendedServiceBookingTool } },
  };

export const multiServiceBookingTool = {
  _id: "1234567890abcdef123456784",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: false },
  mapkitAuthenticationToken: "testToken",
  name: "Multi Service Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [
      { cursor: defaultService._id, node: defaultService },
      { cursor: multipleServices[0]._id, node: multipleServices[0] },
    ],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const multiServiceBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: multiServiceBookingTool._id },
    },
    result: { data: { bookingTool: multiServiceBookingTool } },
  };

export const singleOrgServiceBookingTool: BookingTool = {
  _id: "1234567890abcdef12345679",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: false },
  mapkitAuthenticationToken: "testToken",
  name: "Test Booking Tool, single org & service",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const tenOrganisationBookingTool = {
  _id: "1234567890abcdef12345681",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: {
    includedCountries: ["UK"],
    maxLocationSearchRange: 900,
    maxLocationsToDisplay: 8,
    minLocationsToDisplay: 3,
    overrideLocationSearchQueryMapping: [
      { from: "Hamilton", to: "Hamilton, Scotland" },
    ],
    searchAndMapEnabled: true,
  },
  mapkitAuthenticationToken: "testToken",
  name: "Ten Org Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: tenOrganisationsPage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const tenOrganisationsBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  tenOrganisationsPage.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: tenOrganisationBookingTool._id,
    };
    if (i !== 0) {
      variables.organisationCursor = tenOrganisationsPage.edges[i - 1].cursor;
    }

    return {
      request: {
        query: GetBookingToolDataDocument,
        variables,
      },
      result: {
        data: {
          bookingTool: {
            ...tenOrganisationBookingTool,
            organisations: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: tenOrganisationsPage.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

export const tenOrganisationNoLocationRestrictionBookingTool = {
  _id: "1234567890abcdef12345682",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: true },
  mapkitAuthenticationToken: "testToken",
  name: "Ten Org - No Location Restrictions Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: tenOrganisationsPage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const tenOrganisationNoLocationRestrictionBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  tenOrganisationsPage.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: tenOrganisationNoLocationRestrictionBookingTool._id,
    };
    if (i !== 0) {
      variables.organisationCursor = tenOrganisationsPage.edges[i - 1].cursor;
    }

    return {
      request: {
        query: GetBookingToolDataDocument,
        variables,
      },
      result: {
        data: {
          bookingTool: {
            ...tenOrganisationNoLocationRestrictionBookingTool,
            organisations: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: tenOrganisationsPage.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

export const genericBookingTool = {
  _id: "GENERIC_BOOKING_TOOL",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  isGenericBookingTool: true,
  mapkitAuthenticationToken: "testToken",
  name: "Genetic Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [
      { cursor: defaultService._id, node: defaultService },
      { cursor: multipleServices[0]._id, node: multipleServices[0] },
    ],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const genericBookingToolSingleService = {
  _id: "GENERIC_BOOKING_TOOL",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  isGenericBookingTool: true,
  mapkitAuthenticationToken: "testToken",
  name: "Genetic Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const noServicesBookingTool = {
  _id: "1234567890abcdef12345643",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  mapkitAuthenticationToken: "testToken",
  name: "No Services Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const noServicesBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: noServicesBookingTool._id },
    },
    result: { data: { bookingTool: noServicesBookingTool } },
  };

export const multiServicesBookingTool = {
  _id: "multiple pages of services",
  forceOrganisationSelection: false,
  forceServiceSelection: true,
  mapkitAuthenticationToken: "testToken",
  name: "Booking Tool with lots of services",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: multipleServices.map((service) => ({
      cursor: service._id,
      node: service,
    })),
    pageInfo: createPageInfo({ total: multipleServices.length }),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const poaServicesBookingTool = {
  _id: "poa service",
  forceOrganisationSelection: false,
  forceServiceSelection: true,
  mapkitAuthenticationToken: "testToken",
  name: "Booking Tool with poa service",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [poaService].map((service) => ({
      cursor: service._id,
      node: service,
    })),
    pageInfo: createPageInfo({ total: 1 }),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const multiPageServicesBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  multiServicesBookingTool.services.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: multiServicesBookingTool._id,
    };
    if (i !== 0) {
      variables.serviceCursor =
        multiServicesBookingTool.services.edges[i - 1].cursor;
    }

    return {
      request: { query: GetBookingToolDataDocument, variables },
      result: {
        data: {
          bookingTool: {
            ...multiServicesBookingTool,
            services: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: multiServicesBookingTool.services.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

export const noOrganisationNoServicesBookingTool = {
  _id: "2234567890abcdef12345689",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  mapkitAuthenticationToken: "testToken",
  name: "No Organisation Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const noOrganisationNoServicesBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: noOrganisationNoServicesBookingTool._id },
    },
    result: { data: { bookingTool: noOrganisationNoServicesBookingTool } },
  };

export const complexDurationAndAttendeesBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: complexDurationAndAttendeesServices.map((service) => ({
      cursor: service._id,
      node: service,
    })),
    pageInfo: createPageInfo({
      total: complexDurationAndAttendeesServices.length,
    }),
  },
};

export const multiTierServiceBookingTool: BookingTool = {
  _id: "1234567890abcdef12345688",
  forceOrganisationSelection: false,
  forceServiceSelection: true,
  mapkitAuthenticationToken: "testToken",
  name: "Booking Tool with 1 multi-tier service",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: multiTierService._id, node: multiTierService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const bookingToolOrgForcedTimezone = {
  ...defaultBookingTool,
  organisations: {
    edges: [
      {
        cursor: organisationForcedTimezone._id,
        node: organisationForcedTimezone,
      },
    ],
    pageInfo: createPageInfo(),
  },
};

export const bookingToolForcedTimezoneRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: defaultBookingTool._id },
    },
    result: { data: { bookingTool: bookingToolOrgForcedTimezone } },
  };
export const serviceUnavailableBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [{ cursor: unavailableService._id, node: unavailableService }],
    pageInfo: createPageInfo(),
  },
};

export const serviceNoDescriptionBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [{ cursor: serviceNoDescription._id, node: serviceNoDescription }],
    pageInfo: createPageInfo(),
  },
};

export const serviceWithUnsafeDescriptionBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [
      {
        cursor: serviceWithUnsafeDescription._id,
        node: serviceWithUnsafeDescription,
      },
    ],
    pageInfo: createPageInfo(),
  },
};

export const organisationWithMapAvailabilityBookingTool = {
  _id: "1234567890abcdef12345679",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: true },
  mapkitAuthenticationToken: "testToken",
  multiSlotBookingFlowEnabled: true,
  name: "Test Booking Tool, single org & service",
  organisationAvailabilityFlow: true,
  organisations: {
    edges: [
      {
        cursor: testOrganisationWithFeatureFlag._id,
        node: testOrganisationWithFeatureFlag,
      },
    ],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const organisationWithMapAvailabilityBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: organisationWithMapAvailabilityBookingTool._id },
    },
    result: {
      data: { bookingTool: organisationWithMapAvailabilityBookingTool },
    },
  };

export const twoOrganisationBookingTool = {
  _id: "1234567890abcdef12345681",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: {
    includedCountries: ["UK"],
    maxLocationSearchRange: 900,
    maxLocationsToDisplay: 5,
    minLocationsToDisplay: 3,
    overrideLocationSearchQueryMapping: [
      { from: "Hamilton", to: "Hamilton, Scotland" },
    ],
    searchAndMapEnabled: true,
  },
  mapkitAuthenticationToken: "testToken",
  name: "Two Org Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: twoOrganisationsPage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const twoOrganisationBookingDifferentTimezonesTool: BookingTool = {
  _id: "1234567890abcdef12345681",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: {
    includedCountries: ["UK"],
    maxLocationSearchRange: 900,
    maxLocationsToDisplay: 5,
    minLocationsToDisplay: 3,
    overrideLocationSearchQueryMapping: [
      { from: "Hamilton", to: "Hamilton, Scotland" },
    ],
    searchAndMapEnabled: true,
  },
  mapkitAuthenticationToken: "testToken",
  name: "Two Org Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: twoOrganisationsDifferentTimezonePage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const twoOrganisationsBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  twoOrganisationsPage.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: twoOrganisationBookingTool._id,
    };
    if (i !== 0) {
      variables.organisationCursor = twoOrganisationsPage.edges[i - 1].cursor;
    }

    return {
      request: {
        query: GetBookingToolDataDocument,
        variables,
      },
      result: {
        data: {
          bookingTool: {
            ...twoOrganisationBookingTool,
            organisations: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: twoOrganisationsPage.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });
